class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      'keyup',
      (evt) => evt.code === 'Escape' && this.close(),
    );
    this.querySelector('#CartDrawer-Overlay').addEventListener(
      'click',
      this.close.bind(this),
    );
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' || !event.target.matches('[name="updates[]"]'))
        return;
      event.preventDefault();
      const input = event.target;
      input.blur();
      requestAnimationFrame(() => input.focus());
    });
    this.setHeaderCartIconAccessibility();
    this.setupQuickViewListeners();

    try {
      if (!this.classList.contains('is-empty')) {
        const firstItem = this.querySelector('.cart-drawer__item');
        if (firstItem) {
          const productId = firstItem.getAttribute('data-product-id');
          if (productId) {
            this.fetchRelatedProducts(productId);
          }
        }
      }
    } catch (error) {
      console.error('Error loading cart drawer recommendations:', error);
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();

      if (window.location.pathname === '/cart') {
        return;
      }
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();

        if (window.location.pathname === '/cart') {
          return;
        }
        this.open(cartLink);
      }
    });
  }

  setupQuickViewListeners() {
    document.addEventListener('quickViewOpened', () => {
      if (this.classList.contains('active')) {
        removeTrapFocus();
      }
    });

    document.addEventListener('quickViewClosed', (event) => {
      if (this.classList.contains('active')) {
        const container = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const triggerButton = event.detail?.triggerButton;
        trapFocus(container, triggerButton);
      }
    });
  }

  syncWithNavPosition() {
    let navTop = 0;
    let navBottom = 0;

    const width = window.innerWidth;
    if (width < 1200) {
      const rect = document
        .getElementById('stickyHeader')
        .getBoundingClientRect();
      navTop = Math.max(0, rect.top);
      navBottom = Math.max(0, rect.bottom);
    }

    this.style.setProperty('--cart-drawer-top', navTop + 'px');

    this.style.setProperty('--cart-drawer-overlay-top', navBottom + 'px');
  }

  open(triggeredBy, shouldDispatchDrawerOpenedEvent = true) {
    this.syncWithNavPosition();

    if (triggeredBy) {
      this.setActiveElement(triggeredBy);
    } else if (!this.activeElement || !this.activeElement.isConnected) {
      this.setActiveElement(document.activeElement);
    }
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.setPageAccessibility(true);

    this.addEventListener(
      'transitionend',
      () => {
        if (this.hasAttribute('data-skip-focus-on-render')) {
          this.removeAttribute('data-skip-focus-on-render');
          return;
        }

        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.cart-drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      {once: true},
    );

    document.body.classList.add('overflow-hidden', 'cart-drawer-open');
    const mainContent = document.querySelector('main');
    const footerContent = document.querySelector('footer');
    if (mainContent) {
      mainContent.setAttribute('inert', '');
    }
    if (footerContent) {
      footerContent.setAttribute('inert', '');
    }

    if (shouldDispatchDrawerOpenedEvent) {
      this.dispatchDrawerOpenedEvent();
    }
  }

  validateCartStateChange() {
    const subtotalElement = this.querySelector(
      '[data-view-cart-validation-total]',
    );
    const cartItems = this.querySelectorAll('[data-view-cart-validation-item]');
    const itemCountElement = this.querySelector(
      '[data-view-cart-validation-count]',
    );
    const subtotalValue = subtotalElement?.getAttribute('data-ga4-subtotal');

    const currentState = {
      item_count: itemCountElement ? parseInt(itemCountElement.textContent) : 0,
      total_price: subtotalValue ? parseFloat(subtotalValue) : 0,
      items_length: cartItems.length,
    };

    const savedState = sessionStorage.getItem('cart_state');
    const previousState = savedState ? JSON.parse(savedState) : null;

    const hasChanged =
      !previousState ||
      previousState.item_count !== currentState.item_count ||
      previousState.total_price !== currentState.total_price ||
      previousState.items_length !== currentState.items_length;

    if (hasChanged) {
      sessionStorage.setItem('cart_state', JSON.stringify(currentState));
    }

    return hasChanged;
  }

  dispatchDrawerOpenedEvent() {
    const hasChanged = this.validateCartStateChange();
    if (hasChanged) {
      document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden', 'cart-drawer-open');
    const mainContent = document.querySelector('main');
    const footerContent = document.querySelector('footer');
    if (mainContent) {
      mainContent.removeAttribute('inert');
    }
    if (footerContent) {
      footerContent.removeAttribute('inert');
    }
    this.setPageAccessibility(false);

    const restoreFocusTarget = this.activeElement;
    const targetToFocus =
      restoreFocusTarget &&
      restoreFocusTarget.isConnected &&
      !restoreFocusTarget.hasAttribute('disabled')
        ? restoreFocusTarget
        : null;
    requestAnimationFrame(() => {
      if (targetToFocus) {
        targetToFocus.focus();
      }
    });
  }

  setPageAccessibility(isOpen) {
    const parent = this.parentElement;
    if (!parent) return;

    const siblings = Array.from(parent.children).filter((child) => {
      if (child === this) return false;
      const tagName = child.tagName?.toLowerCase();
      return (
        tagName !== 'style' &&
        tagName !== 'script' &&
        tagName !== 'petivity-quick-view'
      );
    });

    siblings.forEach((sibling) => {
      if (isOpen) {
        if (!sibling.hasAttribute('aria-hidden')) {
          sibling.setAttribute('inert', '');
          sibling.setAttribute('aria-hidden', 'true');
          sibling.dataset.cartDrawerAriaHidden = 'true';
        }
      } else {
        if (sibling.dataset.cartDrawerAriaHidden === 'true') {
          sibling.removeAttribute('aria-hidden');
          sibling.removeAttribute('inert');
          delete sibling.dataset.cartDrawerAriaHidden;
        }
      }
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute(
        'aria-controls',
        cartDrawerNote.nextElementSibling.id,
      );
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute(
        'aria-expanded',
        !event.currentTarget.closest('details').hasAttribute('open'),
      );
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(
        parsedState.sections[section.id],
        section.selector,
      );
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener(
        'click',
        this.close.bind(this),
      );
      this.open(null, false);
      this.announceCartUpdate();

      if (!this.classList.contains('is-empty')) {
        const firstItem = this.querySelector('.cart-drawer__item');
        if (firstItem) {
          const productId = firstItem.getAttribute('data-product-id');
          if (productId) {
            this.fetchRelatedProducts(productId);
          }
        }
      }
    });
  }

  announceCartUpdate() {
    const announcement = this.querySelector('#CartDrawer-UpdateAnnouncement');
    if (!announcement) return;

    const countElement = this.querySelector(
      '[data-view-cart-validation-count]',
    );
    const subtotalElement = this.querySelector(
      '.cart-drawer__details-subtotal-value',
    );
    const itemCount = countElement ? parseInt(countElement.textContent, 10) : 0;
    const itemText = itemCount === 1 ? 'item' : 'items';
    const subtotalText = subtotalElement
      ? subtotalElement.textContent.trim()
      : '';
    const message = subtotalText
      ? `Cart updated. ${itemCount} ${itemText} in cart. Subtotal ${subtotalText}.`
      : `Cart updated. ${itemCount} ${itemText} in cart.`;

    announcement.textContent = '';
    if (itemCount) {
      setTimeout(() => {
        announcement.textContent = message;
        setTimeout(() => {
          announcement.textContent = '';
        }, 3000);
      }, 200);
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  fetchRelatedProducts(productId) {
    const relatedProductsContainer = this.querySelector(
      '[data-cart-drawer-product-recommendations]',
    );
    if (!relatedProductsContainer) return;

    fetch(
      `/recommendations/products?section_id=cart-drawer-recommendations&product_id=${productId}&limit=6`,
    )
      .then((response) => response.text())
      .then((text) => {
        const wrapper = document.createElement('div');
        wrapper.innerHTML = text;

        relatedProductsContainer.innerHTML = wrapper.innerHTML;

        const slides = this.querySelectorAll(
          `[data-id].petivity-product-card__full-container`,
        );
        if (slides) {
          const observer = new IntersectionObserver(
            (entries, observer) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  setTimeout(() => {
                    entry.target.classList.remove('show_placeholder');
                    observer.unobserve(entry.target);
                  }, 500);
                }
              });
            },
            {threshold: 0.5},
          );

          slides.forEach((slide) => {
            observer.observe(slide);
          });
        }
      })
      .catch((error) => {
        console.error(
          'Error when searching related products from minicart:',
          error,
        );
      });
  }

  announceCartAdding(message = 'Adding product to the cart') {
    const announcement = this.querySelector('#CartDrawer-UpdateAnnouncement');
    if (!announcement) return;

    announcement.textContent = '';

    setTimeout(() => {
      announcement.textContent = message;
      setTimeout(() => {
        announcement.textContent = '';
      }, 2000);
    }, 200);
  }
}

customElements.define('cart-drawer', CartDrawer);
class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    const isModularHeader =
      document.querySelector('.section-nav-icon-links') !== null;
    const headerSection = isModularHeader ? 'header-icon-links' : 'header';

    return [
      {
        // cart-Drawer
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        // header
        id: 'cart-icon-bubble',
        section: headerSection,
        selector: '.global-header__utility-link--cart',
      },
      {
        // cart-Drawer counter
        id: 'cart-icon-bubble-drawer',
        section: 'cart-drawer',
        selector: '.global-header__utility-link--cart-bubble',
      },
    ];
  }

  updateQuantity(line, quantity, name) {
    this.enableLoading(line);
    const item = document.getElementById(`CartDrawer-Item-${line}`);
    const itemKey = item ? item.getAttribute('data-key') : null;
    const productSampleVariantId = item
      ? item.getAttribute('data-product-sample-variant-id')
      : null;

    if (!itemKey) {
      console.error(
        'Item key not found for line:',
        line,
        '- Element exists:',
        !!item,
      );
      this.disableLoading();
      return;
    }

    let body = {};
    let fetchApi = routes.cart_change_url;
    let sampleProduct = '';
    if (productSampleVariantId) {
      sampleProduct = document.querySelector(
        `.cart-item[data-ga4-variant="${productSampleVariantId}"]`,
      );

      if (!sampleProduct) return;
      body = JSON.stringify({
        updates: {
          [itemKey]: quantity,
          [sampleProduct.dataset.key]: quantity,
        },
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });
      fetchApi = routes.cart_update_url;
    } else {
      body = JSON.stringify({
        id: itemKey,
        quantity,
        sections: this.getSectionsToRender().map((section) => section.section),
        sections_url: window.location.pathname,
      });
    }

    fetch(`${fetchApi}`, {...fetchConfig(), ...{body}})
      .then((response) => {
        return response.text().then((text) => {
          let processedText = text;
          let displayInventoryError = false;
          try {
            const parsed = JSON.parse(text);
            const requestedQty = parseInt(quantity, 10);
            const actualItem = parsed.items?.find((i) => i.key === itemKey);

            // Case 1 — HTTP 422: Shopify explicitly rejects the quantity and returns { "errors": "Only X items were added to your cart due to availability." }.
            const isAvailabilityError =
              typeof parsed.errors === 'string' &&
              /^Only \d+ item(?:s)? (?:was|were) added to your cart due to availability\.$/.test(
                parsed.errors,
              );

            // Case 2 — HTTP 200 with silent quantity cap: Shopify accepts the request but silently reduces the quantity to available stock without setting an errors field.
            const isQuantityCapped =
              actualItem &&
              !isNaN(requestedQty) &&
              requestedQty > actualItem.quantity &&
              !parsed.errors;

            if (isQuantityCapped) {
              const q = actualItem.quantity;
              parsed.errors = `Only ${q} item${q === 1 ? '' : 's'} ${q === 1 ? 'was' : 'were'} added to your cart due to availability.`;
              processedText = JSON.stringify(parsed);
            }

            // Both Case 1 and Case 2 are displayed the same way — via updateLiveRegions.
            displayInventoryError = isQuantityCapped || isAvailabilityError;
          } catch (_) {}

          if (!response.ok) {
            // Case 1 (422 availability): return processedText instead of throwing so the message is shown inline on the cart item via updateLiveRegions.
            if (displayInventoryError) return processedText;

            let message =
              window.cartStrings?.error ||
              'There was an error while updating your cart. Please try again.';
            try {
              const err = JSON.parse(text);
              if (err.description || err.message)
                message = err.description || err.message;
            } catch (_) {}
            throw new Error(message);
          }

          return processedText;
        });
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        if (parsedState.status === 422) {
          throw new Error(
            parsedState.description ||
              parsedState.message ||
              window.cartStrings?.error ||
              'There was an error while updating your cart. Please try again.',
          );
        }

        this.classList.toggle('is-empty', parsedState.item_count === 0);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');

        if (cartFooter)
          cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
        if (cartDrawerWrapper)
          cartDrawerWrapper.classList.toggle(
            'is-empty',
            parsedState.item_count === 0,
          );

        if (parsedState.sections) {
          this.getSectionsToRender().forEach((section) => {
            try {
              if (section.id === 'CartDrawer') {
                const newContent = this.getSectionInnerHTML(
                  parsedState.sections[section.section],
                  section.selector,
                );
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = newContent;

                if (parsedState.item_count === 0) {
                  const elementToReplace =
                    document
                      .getElementById(section.id)
                      .querySelector(section.selector) ||
                    document.getElementById(section.id);
                  elementToReplace.innerHTML = newContent;
                } else {
                  const isRemoval = parseInt(quantity) === 0;
                  if (isRemoval) {
                    const itemToRemove = document.getElementById(
                      `CartDrawer-Item-${line}`,
                    );
                    if (itemToRemove) {
                      const nextItem = itemToRemove.nextElementSibling;
                      const prevItem = itemToRemove.previousElementSibling;
                      const nextFocusTarget = nextItem || prevItem;

                      let nextButton = null;
                      if (nextFocusTarget) {
                        nextButton =
                          nextFocusTarget.querySelector(
                            'petivity-quick-view-trigger button',
                          ) ||
                          nextFocusTarget.querySelector(
                            'cart-remove-button button',
                          );
                      }

                      itemToRemove.remove();

                      if (nextButton) {
                        nextButton.focus();
                      }

                      if (productSampleVariantId) {
                        sampleProduct.remove();
                      }
                    }
                  }

                  // Update Item (keys, GA4 attributes, quantities, prices)
                  const oldItemsWrapper = document.querySelector(
                    '.cart-drawer__cart-items-wrapper',
                  );
                  const newItemsWrapper = tempDiv.querySelector(
                    '.cart-drawer__cart-items-wrapper',
                  );

                  if (oldItemsWrapper && newItemsWrapper) {
                    const oldItems =
                      oldItemsWrapper.querySelectorAll('.cart-drawer__item');
                    const newItems =
                      newItemsWrapper.querySelectorAll('.cart-drawer__item');

                    // Update each item's (quantity/price/GA4 attributes)
                    oldItems.forEach((oldItem, index) => {
                      const newItem = newItems[index];
                      if (newItem) {
                        oldItem.setAttribute(
                          'data-key',
                          newItem.getAttribute('data-key'),
                        );

                        // Update all GA4 data attributes dynamically
                        Array.from(newItem.attributes).forEach((attr) => {
                          if (attr.name.startsWith('data-ga4-')) {
                            oldItem.setAttribute(attr.name, attr.value);
                          }
                        });

                        // Update quick view trigger data-line-key
                        const oldTrigger = oldItem.querySelector(
                          'petivity-quick-view-trigger',
                        );
                        const newTrigger = newItem.querySelector(
                          'petivity-quick-view-trigger',
                        );
                        if (oldTrigger && newTrigger) {
                          const newLineKey =
                            newTrigger.getAttribute('data-line-key');
                          if (newLineKey) {
                            oldTrigger.setAttribute(
                              'data-line-key',
                              newLineKey,
                            );
                          }
                          const newQuantity =
                            newTrigger.getAttribute('data-quantity');
                          if (newQuantity) {
                            oldTrigger.setAttribute(
                              'data-quantity',
                              newQuantity,
                            );
                          }
                        }

                        // Update input value
                        if (!isRemoval) {
                          const oldInput =
                            oldItem.querySelector('.quantity__input');
                          const newInput =
                            newItem.querySelector('.quantity__input');
                          if (
                            oldInput &&
                            newInput &&
                            oldInput.value !== newInput.value
                          ) {
                            oldInput.value = newInput.value;
                          }
                        }

                        // Update price wrapper
                        const oldPrice = oldItem.querySelector(
                          '.cart-item__price-wrapper',
                        );
                        const newPrice = newItem.querySelector(
                          '.cart-item__price-wrapper',
                        );
                        if (oldPrice && newPrice) {
                          oldPrice.innerHTML = newPrice.innerHTML;
                        }

                        // Update error message
                        const oldError =
                          oldItem.querySelector('.cart-item__error');
                        const newError =
                          newItem.querySelector('.cart-item__error');
                        if (oldError && newError) {
                          const oldErrorText = oldError.querySelector(
                            '.cart-item__error-text',
                          );
                          const newErrorText = newError.querySelector(
                            '.cart-item__error-text',
                          );

                          if (oldErrorText && newErrorText) {
                            oldErrorText.textContent = newErrorText.textContent;
                          }

                          if (newError.hasAttribute('hidden')) {
                            oldError.setAttribute('hidden', '');
                          } else {
                            oldError.removeAttribute('hidden');
                          }
                        }
                      }
                    });
                  }

                  // Update footer subtotal
                  const oldSubtotal = document.querySelector(
                    '.cart-drawer__details-subtotal-value',
                  );
                  const newSubtotal = tempDiv.querySelector(
                    '.cart-drawer__details-subtotal-value',
                  );

                  if (oldSubtotal && newSubtotal) {
                    oldSubtotal.textContent = newSubtotal.textContent;
                    if (newSubtotal.hasAttribute('data-ga4-subtotal')) {
                      oldSubtotal.setAttribute(
                        'data-ga4-subtotal',
                        newSubtotal.getAttribute('data-ga4-subtotal'),
                      );
                    }
                  }
                }
              } else {
                const sectionElement = document.getElementById(section.id);
                if (sectionElement) {
                  const elementToReplace =
                    sectionElement.querySelector(section.selector) ||
                    sectionElement;
                  if (
                    elementToReplace &&
                    parsedState.sections[section.section]
                  ) {
                    elementToReplace.innerHTML = this.getSectionInnerHTML(
                      parsedState.sections[section.section],
                      section.selector,
                    );
                  }
                }
              }
            } catch (error) {
              console.error('Error updating section:', section.id, error);
            }
          });
        }

        this.updateLiveRegions(line, parsedState);

        // When Shopify returns 422 with an availability error, the response has no
        // sections — so the data-key attributes are never updated. If Shopify's
        // internal processing (Flow/script) changed the item key, the stale key
        // will cause the next update to fail with "no valid id or line parameter".
        // Re-fetch the current cart sections to keep data-key in sync.
        if (!parsedState.sections && typeof parsedState.errors === 'string') {
          this.syncCartItemKeys();
        }

        if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(
            cartDrawerWrapper.querySelector('.drawer__inner-empty'),
            cartDrawerWrapper.querySelector('a'),
          );
        }

        this.disableLoading();
      })
      .catch((error) => {
        console.error('Cart update error:', error);
        this.querySelectorAll('.loading-overlay').forEach((overlay) =>
          overlay.classList.add('hidden'),
        );
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors)
          errors.textContent =
            error.message ||
            window.cartStrings?.error ||
            'There was an error while updating your cart. Please try again.';
        this.disableLoading();
      });
  }

  syncCartItemKeys() {
    fetch(`/?sections=cart-drawer`)
      .then((r) => r.json())
      .then((fetchedSections) => {
        if (!fetchedSections['cart-drawer']) return;
        const parsed = new DOMParser().parseFromString(
          fetchedSections['cart-drawer'],
          'text/html',
        );
        const newItems = parsed.querySelectorAll('.cart-drawer__item');
        const oldItems = this.querySelectorAll('.cart-drawer__item');
        oldItems.forEach((oldItem, index) => {
          const newItem = newItems[index];
          if (newItem) {
            oldItem.setAttribute('data-key', newItem.getAttribute('data-key'));
          }
        });
      })
      .catch(() => {});
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
